import React, { useState, useEffect } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';
import { css, cssMap as unboundCssMap, keyframes } from '@compiled/react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Text, Stack, Inline, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives/responsive';
import { RovoIcon } from '@atlaskit/logo';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	useSetOnboardingState,
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { ROVO_AWARENESS_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-rovo-awareness.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { useToggleConversationAssistant } from '@confluence/conversation-assistant/entry-points/useToggleConversationAssistant.ts';

import { CarouselModal } from '../CarouselModal.tsx';

import { RovoTelepointer } from './assets/telepointerRovo.tsx';
import { GreySwirl } from './assets/greySwirl.tsx';

const ROVO_ACTIONS = [
	'getYouToSpeed',
	'createAutomations',
	'brainstormIdeas',
	'synthesizeUpdates',
	'writeJobDescriptions',
	'identifyRisks',
	'makePageLookGreat',
	'connectDots',
	'transformTickets',
	'writeReleaseNotes',
] as const;

const i18n = defineMessages({
	bannerHeader: {
		id: 'rovo-awareness.banner.header',
		defaultMessage:
			'Ask Rovo to <scrollingMessage>{action, select, getYouToSpeed {get you up to speed...} createAutomations {create automations...} brainstormIdeas {brainstorm ideas...} synthesizeUpdates {synthesize project updates...} writeJobDescriptions {write job descriptions...} identifyRisks {identify and mitigate risks...} makePageLookGreat {make your page look great...} connectDots {connect the dots...} transformTickets {transform tickets into plans...} writeReleaseNotes {write release notes...} other {help with your work...}}</scrollingMessage>',
		description:
			'Header text displayed in the Rovo awareness banner on Confluence home page. Used to encourage users to try asking Rovo for help with various tasks. "Rovo" is a product name',
	},
	bannerDescription: {
		id: 'rovo-awareness.banner.description',
		defaultMessage:
			'Let your AI teammate do the heavy lifting so you can focus on what matters. Just ask.',
		description: 'Body text appearing the Rovo awareness banner on Confluence home page.',
	},
	bannerAskAnythingCTA: {
		id: 'rovo-awareness.banner.ask-anything-cta',
		defaultMessage: 'Ask Rovo',
		description: 'Text that appears in a primary button',
	},
	bannerMoreIdeasCTA: {
		id: 'rovo-awareness.banner.more-ideas-cta',
		defaultMessage: 'More ideas',
		description: 'Text that appears in a default button',
	},
	searchBarImageText: {
		id: 'rovo-awareness.banner.search-bar-image-text',
		defaultMessage: 'If you Rovo, you know...',
		description: "Text that appears in a decorative search bar that doesn't have any functionality",
	},
	dismissBannerCTA: {
		id: 'rovo-awareness.banner.dismiss-banner-cta',
		defaultMessage: 'Close banner',
		description: 'Text that appears as a label and tooltip for an icon button to close the banner',
	},
});

const containerStyles = css({
	position: 'relative',
	// We need the color to be hard coded as the design is the same between light and dark themes
	// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
	backgroundColor: '#101214',
	color: token('color.text.inverse'),
	overflow: 'hidden',
	borderRadius: token('radius.xlarge'),
	paddingTop: token('space.500'),
	paddingBottom: token('space.500'),
	display: 'grid',
	gridTemplateColumns: '1fr',
	border: token('color.border'),
	containerName: 'rovo-awareness-home-banner',
	containerType: 'inline-size',
	[media.above.md]: {
		gridTemplateColumns: '1fr 1fr',
	},
});

const slideInAnimation = keyframes({
	from: { opacity: 0, transform: 'translateY(20px)' },
	to: { opacity: 1, transform: 'translateY(0)' },
});

const slideOut = keyframes({
	from: { opacity: 1, transform: 'translateY(0)' },
	to: { opacity: 0, transform: 'translateY(-20px)' },
});

const animationStyles = css({
	position: 'absolute',
	top: 0,
	bottom: 0,
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&.slide-enter': {
		position: 'absolute',
		opacity: 0,
		transform: 'translateY(40px)',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&.slide-enter-active': {
		position: 'absolute',
		animation: `${slideInAnimation} 0.25s forwards`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&.slide-exit': {
		position: 'absolute',
		opacity: 1,
		transform: 'translateY(0)',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&.slide-exit-active': {
		position: 'absolute',
		animation: `${slideOut} 0.25s forwards`,
	},
});

/**
 * Every single eslint disable here is based on the design of the banner being the same between dark and light mode. This means
 * we can't use any tokens as they automatically update between the two themes as well as most ADS components as they also use tokens internally.
 * This means we can't use Headings, Texts, and Buttons (all variations of buttons).
 * 2.
 */
const unboundStyles = unboundCssMap({
	rightContainerStyles: {
		position: 'relative',
		overflow: 'hidden',
		display: 'flex',
		alignItems: 'center',
		flexDirection: 'row-reverse',
		zIndex: 1,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-container-queries
		'@container rovo-awareness-home-banner (width < 60rem)': {
			display: 'none',
		},
	},
	headerStyles: {
		// eslint-disable-next-line  @atlaskit/design-system/use-tokens-typography
		fontSize: '24px',
	},
	whiteTextColor: {
		// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
		color: 'white',
	},
	searchBarPositioner: {
		display: 'flex',
		justifyContent: 'center',
		flexDirection: 'row-reverse',
	},
	searchBarReverse: {
		width: '100%',
		display: 'flex',
	},
	searchBarStyles: {
		overflow: 'hidden',
		borderTopLeftRadius: token('radius.small'),
		borderBottomLeftRadius: token('radius.small'),
		padding: token('space.200'),
		// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: 'white',
		maxWidth: '360px',
		whiteSpace: 'nowrap',
	},
	rovoTelepointerSpacerStyles: {
		marginRight: token('space.050'),
	},
	greySwirlContainerStyles: {
		position: 'absolute',
		zIndex: '0',
		right: 0,
		height: '100%',
	},
	greySwirlStyles: {
		height: '100%',
	},
	rovoGradientText: {
		backgroundImage:
			'linear-gradient(90deg, rgba(41, 42, 46, 0.70) -9.6%, rgba(31, 105, 218, 0.70) -1.18%, rgba(193, 108, 212, 0.70) 26.1%, rgba(245, 161, 23, 0.70) 69.11%, rgba(121, 174, 68, 0.70) 93.71%, rgba(41, 42, 46, 0.70) 112.12%)',
		backgroundClip: 'text',
		color: 'transparent',
	},
	buttonStyles: {
		// We need the color to be hard coded as the design is the same between light and dark themes
		// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
		color: 'white',
		padding: '6px 12px',
		border: 'none',
		borderRadius: token('radius.medium'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		height: '32px',
		boxSizing: 'border-box',
		cursor: 'pointer',
	},
	primaryButtonStyles: {
		// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#0d66e5',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&:hover': {
			// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
			backgroundColor: '#0255cb',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&:active': {
			// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
			backgroundColor: '#144794',
		},
	},
	primaryButtonPressedStyles: {
		// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#144794',
	},
	defaultButtonStyles: {
		// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
		color: 'white',
		border: `1px solid ${token('color.border.accent.gray')}`,
		// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#091E420F',

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&:hover': {
			// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
			color: 'white',
			// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
			backgroundColor: '#A1BDD914',
		},
	},

	dismissIconStyles: {
		top: token('space.150'),
		right: token('space.150'),
		position: 'absolute',
		cursor: 'pointer',
		zIndex: 1,

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > button': {
			// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
			color: 'white',
			// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
			backgroundColor: '#091E420F',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'&:hover': {
				// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
				backgroundColor: '#A1BDD914',
				// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
				color: 'white',
			},
			'&:focus': {
				// eslint-disable-next-line  @atlaskit/design-system/ensure-design-token-usage
				color: 'white',
			},
		},
	},
});

const styles = cssMap({
	contentStyles: {
		minWidth: '576px',
		paddingLeft: token('space.500'),
		paddingRight: token('space.500'),
		zIndex: 1,
	},
	headerContainerStyles: {
		overflow: 'hidden',
		height: '100%',
	},
	inlineBlockStyles: {
		display: 'inline-block',
	},
	relativeStyles: {
		position: 'relative',
	},
});

const RovoAwarenessHomeBannerWrapper = () => {
	const { data, loading, error } = useGetOnboardingState(
		Object.values(ROVO_AWARENESS_ONBOARDING_STATE_KEYS),
	);
	const { hasDismissedHomeBanner } = deserializeState(data);
	if (hasDismissedHomeBanner || loading || error) {
		return <>{error && <ErrorDisplay error={error} />}</>;
	}

	return <RovoAwarenessHomeBannerComponent />;
};

const RovoAwarenessHeading = () => {
	const [currentActionIndex, setCurrentActionIndex] = useState(0);
	useEffect(() => {
		if (currentActionIndex > ROVO_ACTIONS.length * 2 - 1) {
			return;
		}
		const interval = setInterval(() => {
			setCurrentActionIndex((prevIndex) => prevIndex + 1);
		}, 2000);

		return () => clearInterval(interval);
	}, [currentActionIndex]);

	return (
		<Flex xcss={styles.headerContainerStyles} alignItems="center">
			<span css={[unboundStyles.headerStyles, unboundStyles.whiteTextColor]}>
				{/* The heading here has heading level font sizes, but a weight of 400 -> can't use headings + ADS tokens break the design */}
				{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
				<FormattedMessage
					{...i18n.bannerHeader}
					values={{
						scrollingMessage: (chunks) => {
							return (
								<TransitionGroup as="span" css={styles.inlineBlockStyles}>
									<CSSTransition key={currentActionIndex} timeout={500} classNames="slide">
										<span css={animationStyles}>{chunks}</span>
									</CSSTransition>
								</TransitionGroup>
							);
						},
						action: ROVO_ACTIONS[currentActionIndex % ROVO_ACTIONS.length],
					}}
				/>
			</span>
		</Flex>
	);
};

const RovoAwarenessHomeBannerComponent: React.FC = () => {
	const { formatMessage } = useIntl();
	const { setOnboardingState } = useSetOnboardingState();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const { toggleConversationAssistant, isConversationAssistantSidebarActive } =
		useToggleConversationAssistant();
	const [isRovoSidebarActiveViaBanner, setIsRovoSidebarActiveViaBanner] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'rovoAwarenessHomeBanner',
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const handleDismiss = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'rovoAwarenessHomeBannerDismissCTA',
				source: 'rovoAwarenessHomeBanner',
			},
		}).fire();
		setOnboardingState({
			key: ROVO_AWARENESS_ONBOARDING_STATE_KEYS.hasDismissedHomeBanner,
			value: 'true',
		});
	};

	const handleAskRovoClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'rovoAwarenessHomeBannerAskRovoCTA',
				source: 'rovoAwarenessHomeBanner',
				attributes: {
					rovoSidebarPreviouslyActive: isConversationAssistantSidebarActive,
				},
			},
		}).fire();
		toggleConversationAssistant();
		setIsRovoSidebarActiveViaBanner(!isConversationAssistantSidebarActive);
	};

	const handleMoreIdeasClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'rovoAwarenessHomeBannerMoreIdeasCTA',
				source: 'rovoAwarenessHomeBanner',
			},
		}).fire();
		setIsModalOpen(true);
	};

	useEffect(() => {
		// scenario when the user closes Rovo chat NOT from the banner's "Ask Rovo" button
		if (!isConversationAssistantSidebarActive) {
			setIsRovoSidebarActiveViaBanner(false);
		}
	}, [isConversationAssistantSidebarActive]);

	return (
		<div css={containerStyles}>
			<Stack xcss={styles.contentStyles} space="space.400">
				<Stack space="space.100">
					<Flex direction="row" alignItems="center" gap="space.100" xcss={styles.relativeStyles}>
						<RovoIcon size="large" appearance="brand" shouldUseNewLogoDesign />
						<RovoAwarenessHeading />
					</Flex>
					<Text size="large">
						{/* Can't use Text as it uses ADS tokens -> breaks the design when switching between light/dark themes*/}
						{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
						<span css={unboundStyles.whiteTextColor}>
							<FormattedMessage {...i18n.bannerDescription} />
						</span>
					</Text>
				</Stack>
				<Inline space="space.050">
					<button
						css={[
							unboundStyles.buttonStyles,
							isConversationAssistantSidebarActive && isRovoSidebarActiveViaBanner
								? unboundStyles.primaryButtonPressedStyles
								: unboundStyles.primaryButtonStyles,
							unboundStyles.whiteTextColor,
						]}
						onClick={handleAskRovoClick}
						data-testid="ask-rovo-button"
						data-pressed={isConversationAssistantSidebarActive && isRovoSidebarActiveViaBanner}
					>
						<FormattedMessage {...i18n.bannerAskAnythingCTA} />
					</button>
					<button
						css={[unboundStyles.buttonStyles, unboundStyles.defaultButtonStyles]}
						onClick={handleMoreIdeasClick}
					>
						<FormattedMessage {...i18n.bannerMoreIdeasCTA} />
					</button>
				</Inline>
			</Stack>
			<div css={unboundStyles.rightContainerStyles}>
				<div css={unboundStyles.searchBarStyles}>
					{/* The heading here has heading level font sizes, but a weight of 400 -> can't use headings */}
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
					<span
						css={[
							unboundStyles.headerStyles,
							unboundStyles.rovoTelepointerSpacerStyles,
							unboundStyles.rovoGradientText,
						]}
					>
						<FormattedMessage {...i18n.searchBarImageText} />
					</span>
					<RovoTelepointer />
				</div>
			</div>
			<div css={unboundStyles.dismissIconStyles}>
				<IconButton
					isTooltipDisabled={false}
					label={formatMessage(i18n.dismissBannerCTA)}
					appearance="subtle"
					icon={CrossIcon}
					onClick={handleDismiss}
				/>
			</div>
			<div css={unboundStyles.greySwirlContainerStyles}>
				<div css={unboundStyles.greySwirlStyles}>
					<GreySwirl />
				</div>
			</div>
			<CarouselModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
		</div>
	);
};

export const RovoAwarenessHomeBanner = () => {
	return (
		<TransparentErrorBoundary attribution={Attribution.FLYWHEEL_FEATURE_DISCOVERY}>
			<RovoAwarenessHomeBannerWrapper />
		</TransparentErrorBoundary>
	);
};
